import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowSquareOut,
  CaretDown,
  DeviceMobile,
  DownloadSimple,
  GithubLogo,
  HandGrabbing,
  HandWaving,
  MoonStars,
  MouseSimple,
  PersonSimple,
  Sparkle,
} from "@phosphor-icons/react";
import { CHARACTERS } from "./data/characters.js";
import {
  advancePlayback,
  initialPlayback,
  isActionDisabled,
  LOOP_DURATIONS,
  PRELOAD_ASSETS,
  playbackAsset,
  playbackDuration,
  releaseDragPlayback,
  requestPlayback,
  startDragPlayback,
} from "./playback.js";

const ACTIONS = [
  { id: "idle", label: "待机", Icon: PersonSimple },
  { id: "wave", label: "挥手", Icon: HandWaving },
  { id: "phone", label: "看手机", Icon: DeviceMobile },
  { id: "sleep", label: "睡觉", Icon: MoonStars },
];

const ACTION_MESSAGES = {
  "idle-loop": "正在安静待机",
  "wave-once": "正在完整播放挥手动作",
  "phone-enter": "正在坐下并拿出手机",
  "phone-loop": "正在看手机，点击角色让她起身",
  "phone-exit": "正在收起手机并起身",
  "sleep-enter": "正在躺下入睡",
  "sleep-loop": "正在睡觉，点击角色将她唤醒",
  "sleep-exit": "正在醒来并回到待机",
  "drag-enter": "拖动时吓了一跳",
  "drag-hold": "拖动中，保持受惊动作",
  "drag-release": "松手后正在生气跺脚",
};

const INITIAL_SCALE = 0.82;

function defaultPosition() {
  return window.innerWidth <= 780 ? { x: 62, y: 66 } : { x: 51, y: 54 };
}

function assetPath(characterId, skinId, action) {
  return `/characters/${characterId}/${skinId}/${action}.webp`;
}

export function App() {
  const [characterId, setCharacterId] = useState(CHARACTERS[0].id);
  const character = useMemo(
    () => CHARACTERS.find((item) => item.id === characterId) ?? CHARACTERS[0],
    [characterId],
  );
  const [skinId, setSkinId] = useState(character.skins[0].id);
  const skin = useMemo(
    () => character.skins.find((item) => item.id === skinId) ?? character.skins[0],
    [character, skinId],
  );
  const [playback, setPlayback] = useState(initialPlayback);
  const [position, setPosition] = useState(defaultPosition);
  const [scale, setScale] = useState(INITIAL_SCALE);
  const [dragging, setDragging] = useState(false);
  const [skinChanging, setSkinChanging] = useState(false);
  const [assetsReady, setAssetsReady] = useState(false);
  const [exitQueued, setExitQueued] = useState(false);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [downloadAccepted, setDownloadAccepted] = useState(false);
  const stageRef = useRef(null);
  const dragRef = useRef(null);
  const playbackTimerRef = useRef(null);
  const deferredActionTimerRef = useRef(null);
  const phaseStartedAtRef = useRef(0);
  const preloadedImagesRef = useRef([]);
  const downloadButtonRef = useRef(null);
  const downloadDialogRef = useRef(null);
  const downloadCheckboxRef = useRef(null);
  const action = playback.action;
  const actorAsset = playbackAsset(playback);

  useEffect(() => {
    setSkinId(character.skins[0].id);
    setPlayback((current) => initialPlayback(current.runId + 1));
    setExitQueued(false);
    window.clearTimeout(deferredActionTimerRef.current);
    setPosition(defaultPosition());
    setScale(INITIAL_SCALE);
    setDownloadDialogOpen(false);
    setDownloadAccepted(false);
  }, [character]);

  useEffect(() => {
    return () => {
      window.clearTimeout(playbackTimerRef.current);
      window.clearTimeout(deferredActionTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!downloadDialogOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusTimer = window.setTimeout(() => downloadCheckboxRef.current?.focus(), 0);
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setDownloadDialogOpen(false);
        setDownloadAccepted(false);
        window.setTimeout(() => downloadButtonRef.current?.focus(), 0);
      }
      if (event.key === "Tab" && downloadDialogRef.current) {
        const focusable = Array.from(
          downloadDialogRef.current.querySelectorAll(
            'a[href], input:not([disabled]), button:not([disabled])',
          ),
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [downloadDialogOpen]);

  useEffect(() => {
    let active = true;
    setAssetsReady(false);
    const images = PRELOAD_ASSETS.map((asset) => {
      const image = new Image();
      image.src = assetPath(character.id, skin.id, asset);
      return image;
    });
    preloadedImagesRef.current = images;
    Promise.all(
      images.map(
        (image) =>
          new Promise((resolve) => {
            const finish = () => {
              if (typeof image.decode === "function") {
                image.decode().catch(() => undefined).finally(resolve);
              } else {
                resolve();
              }
            };
            if (image.complete) {
              finish();
            } else {
              image.onload = finish;
              image.onerror = resolve;
            }
          }),
      ),
    ).then(() => {
      if (active) setAssetsReady(true);
    });
    return () => {
      active = false;
    };
  }, [character.id, skin.id]);

  function chooseAction(nextAction) {
    if (
      playback.action === "phone" &&
      playback.phase === "loop" &&
      (nextAction === "phone" || nextAction === "idle")
    ) {
      if (exitQueued) return;
      const cycleDuration = LOOP_DURATIONS["phone-loop"];
      const elapsed = Math.max(0, performance.now() - phaseStartedAtRef.current);
      const remaining = cycleDuration - (elapsed % cycleDuration);
      setExitQueued(true);
      deferredActionTimerRef.current = window.setTimeout(() => {
        setPlayback((current) => requestPlayback(current, nextAction));
        setExitQueued(false);
      }, remaining <= 32 ? 0 : remaining);
      return;
    }
    window.clearTimeout(deferredActionTimerRef.current);
    setExitQueued(false);
    setPlayback((current) => requestPlayback(current, nextAction));
  }

  function schedulePhaseCompletion() {
    window.clearTimeout(playbackTimerRef.current);
    phaseStartedAtRef.current = performance.now();
    const duration = playbackDuration(playback);
    if (!duration) return;
    const loadedRunId = playback.runId;
    playbackTimerRef.current = window.setTimeout(() => {
      setPlayback((current) =>
        current.runId === loadedRunId ? advancePlayback(current) : current,
      );
    }, duration);
  }

  function chooseSkin(nextSkin) {
    if (nextSkin === skinId) return;
    window.clearTimeout(deferredActionTimerRef.current);
    setExitQueued(false);
    setSkinChanging(true);
    window.setTimeout(() => {
      setSkinId(nextSkin);
      setPlayback((current) => initialPlayback(current.runId + 1));
      setSkinChanging(false);
    }, 260);
  }

  function startDrag(event) {
    if (!stageRef.current || !assetsReady) return;
    const isPersistentLoop =
      (action === "phone" || action === "sleep") && playback.phase === "loop";
    if (isPersistentLoop) {
      chooseAction(action);
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      position,
      activated: false,
    };
    setDragging(true);
  }

  function moveDrag(event) {
    if (!dragRef.current || !stageRef.current) return;
    const rect = stageRef.current.getBoundingClientRect();
    const deltaX = event.clientX - dragRef.current.startX;
    const deltaY = event.clientY - dragRef.current.startY;
    const distance = Math.hypot(deltaX, deltaY);
    if (!dragRef.current.activated && distance >= 8) {
      dragRef.current.activated = true;
      setPlayback((current) => startDragPlayback(current));
    }
    setPosition({
      x: Math.min(86, Math.max(14, dragRef.current.position.x + (deltaX / rect.width) * 100)),
      y: Math.min(70, Math.max(34, dragRef.current.position.y + (deltaY / rect.height) * 100)),
    });
  }

  function endDrag(event, allowReaction = true) {
    if (!dragRef.current) return;
    const activated = dragRef.current.activated;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragRef.current = null;
    setDragging(false);
    if (activated && allowReaction) {
      setPlayback((current) => releaseDragPlayback(current));
    } else if (activated) {
      setPlayback((current) => initialPlayback(current.runId + 1));
    }
  }

  function resizeCharacter(event) {
    event.preventDefault();
    const step = event.deltaY < 0 ? 0.08 : -0.08;
    setScale((value) => Math.min(1, Math.max(0.68, value + step)));
  }

  function openDownloadDialog() {
    setDownloadAccepted(false);
    setDownloadDialogOpen(true);
  }

  function closeDownloadDialog() {
    setDownloadDialogOpen(false);
    setDownloadAccepted(false);
    window.setTimeout(() => downloadButtonRef.current?.focus(), 0);
  }

  function startDownload() {
    if (!downloadAccepted) return;
    const downloadUrl = character.downloadUrl;
    closeDownloadDialog();
    window.location.assign(downloadUrl);
  }

  return (
    <main className="experience-shell">
      <section
        className="character-stage"
        ref={stageRef}
        onWheel={resizeCharacter}
        aria-label={`${character.name}浏览器互动舞台`}
      >
        <header className="stage-header">
          <a
            className="brand"
            href="https://github.com/super-idol-hub"
            target="_blank"
            rel="noreferrer"
          >
            <span className="brand-mark" aria-hidden="true">
              SI
            </span>
            <span>
              <strong>SUPER IDOL HUB</strong>
              <small>Windows 互动角色</small>
            </span>
          </a>
          <span className="browser-badge">浏览器体验</span>
        </header>

        <div className="stage-copy">
          <p className="eyebrow">{character.fullName}</p>
          <h1>{character.name}</h1>
          <p>{character.tagline}</p>
        </div>

        <div className="quick-hints" aria-label="互动提示">
          <span>
            <HandGrabbing weight="regular" />
            拖动位置
          </span>
          <span>
            <MouseSimple weight="regular" />
            滚轮缩放
          </span>
          <span>
            <Sparkle weight="regular" />
            点击互动
          </span>
        </div>

        <div className="ambient-window" aria-hidden="true" />
        <div className="floor-line" aria-hidden="true" />

        <button
          className={`character-actor ${dragging ? "is-dragging" : ""} ${
            skinChanging ? "is-changing" : ""
          }`}
          style={{
            left: `${position.x}%`,
            top: `${position.y}%`,
          }}
          type="button"
          aria-label={`拖动${character.name}，点击触发互动`}
          onPointerDown={startDrag}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerCancel={(event) => endDrag(event, false)}
          onDoubleClick={() => assetsReady && chooseAction("wave")}
        >
          <img
            src={assetPath(character.id, skin.id, actorAsset)}
            alt={`${character.name}，${skin.name}，${
              ACTIONS.find((item) => item.id === action)?.label ?? "拖动反应"
            }`}
            draggable="false"
            style={{ transform: `scale(${scale})` }}
            onLoad={schedulePhaseCompletion}
          />
        </button>

        <div className="action-status" role="status" aria-live="polite">
          <span className="status-dot" />
          {assetsReady
            ? exitQueued
              ? "正在完成当前手机动作，随后收起手机起身"
              : ACTION_MESSAGES[`${action}-${playback.phase}`]
            : "正在预加载源码动作…"}
        </div>

        <p className="stage-footnote">
          网页保留少量完整动作：挥手会回到待机，看手机和睡觉可点击角色完整结束。
        </p>
      </section>

      <aside className="control-rail">
        <div className="control-scroll">
          <section className="control-section character-picker">
            <div className="section-heading">
              <span>正在体验</span>
              <span>{String(CHARACTERS.findIndex((item) => item.id === character.id) + 1).padStart(2, "0")}</span>
            </div>
            <label className="select-shell">
              <span className="sr-only">选择互动角色</span>
              <select
                value={character.id}
                onChange={(event) => setCharacterId(event.target.value)}
              >
                {CHARACTERS.map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.name} · {item.person}
                  </option>
                ))}
              </select>
              <CaretDown aria-hidden="true" />
            </label>
          </section>

          <section className="control-section">
            <div className="section-heading">
              <span>选择造型</span>
              <span>{character.skins.length} 套</span>
            </div>
            <div className={`skin-grid skin-count-${character.skins.length}`}>
              {character.skins.map((item) => (
                <button
                  type="button"
                  className={item.id === skin.id ? "skin-option is-active" : "skin-option"}
                  key={item.id}
                  onClick={() => chooseSkin(item.id)}
                  aria-pressed={item.id === skin.id}
                >
                  <img
                    src={assetPath(character.id, item.id, "preview")}
                    alt=""
                    aria-hidden="true"
                  />
                  <span>{item.name}</span>
                </button>
              ))}
            </div>
          </section>

          <section className="control-section">
            <div className="section-heading">
              <span>完整动作体验</span>
              <span>正式发行帧</span>
            </div>
            <div className="action-grid">
              {ACTIONS.map(({ id, label, Icon }) => (
                <button
                  type="button"
                  className={action === id ? "action-option is-active" : "action-option"}
                  key={id}
                  onClick={() => chooseAction(id)}
                  aria-pressed={action === id}
                  disabled={
                    !assetsReady || exitQueued || isActionDisabled(playback, id)
                  }
                  style={
                    action === id
                      ? {
                          color: "#fff",
                          borderColor: "#f36d62",
                          background: "#f36d62",
                        }
                      : undefined
                  }
                >
                  <Icon weight={action === id ? "fill" : "regular"} />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </section>

          <section className="control-section project-summary">
            <p className="eyebrow">关于 {character.name}</p>
            <h2>{skin.name}</h2>
            <p>{character.description}</p>
            <dl>
              <div>
                <dt>版本</dt>
                <dd>{character.version}</dd>
              </div>
              <div>
                <dt>平台</dt>
                <dd>Windows</dd>
              </div>
              <div>
                <dt>开发者</dt>
                <dd>Anbunensi</dd>
              </div>
            </dl>
          </section>
        </div>

        <footer className="download-panel">
          <button
            ref={downloadButtonRef}
            type="button"
            className="download-button"
            onClick={openDownloadDialog}
            aria-haspopup="dialog"
          >
            <DownloadSimple weight="bold" />
            <span>
              <strong>下载 Windows 版</strong>
              <small>确认使用边界后直接下载 .exe</small>
            </span>
            <DownloadSimple weight="bold" />
          </button>
          <div className="footer-links">
            <a href={character.repoUrl} target="_blank" rel="noreferrer">
              <GithubLogo weight="fill" />
              项目源码
            </a>
            <span>免费 · 非官方 · 非商业</span>
          </div>
        </footer>
      </aside>

      {downloadDialogOpen ? (
        <div className="download-dialog-backdrop" onPointerDown={closeDownloadDialog}>
          <section
            ref={downloadDialogRef}
            className="download-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="download-dialog-title"
            aria-describedby="download-terms-details"
            onPointerDown={(event) => event.stopPropagation()}
          >
            <div className="download-dialog-heading">
              <div className="download-dialog-mark" aria-hidden="true">
                <DownloadSimple weight="bold" />
              </div>
              <div>
                <p className="eyebrow">下载前确认</p>
                <h2 id="download-dialog-title">{character.name}使用边界</h2>
              </div>
            </div>

            <div id="download-terms-details" className="download-terms">
              <p>
                该 Windows 互动角色为非官方、非商业项目。下载前请确认你理解并同意：
              </p>
              <ul>
                <li>仅限个人、非商业用途，不得出售、收费分发或用于商业宣传。</li>
                <li>不得二次开发、修改、反编译、拆包、重制或再次发布。</li>
                <li>不得冒充官方；人物姓名、肖像及相关权利归各自权利人所有。</li>
                <li>如权利人提出要求，应立即停止使用并删除相关文件。</li>
              </ul>
              <a href={character.repoUrl} target="_blank" rel="noreferrer">
                查看项目仓库中的完整说明
                <ArrowSquareOut weight="bold" />
              </a>
            </div>

            <label className="download-consent">
              <input
                ref={downloadCheckboxRef}
                type="checkbox"
                checked={downloadAccepted}
                onChange={(event) => setDownloadAccepted(event.target.checked)}
              />
              <span>我已阅读并同意以上使用边界及项目仓库中的完整说明。</span>
            </label>

            <div className="download-dialog-actions">
              <button type="button" className="dialog-cancel" onClick={closeDownloadDialog}>
                取消
              </button>
              <button
                type="button"
                className="dialog-confirm"
                disabled={!downloadAccepted}
                onClick={startDownload}
              >
                <DownloadSimple weight="bold" />
                同意并下载 .exe
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </main>
  );
}
